import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
  ValidationErrors
} from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { Entry } from 'contentful';

import { AuthService } from '../../services/auth/auth.service';
import { LocalizationService } from '../../services/localization/localization.service';
import { GlobalContentService } from '../../services/global-content/global-content.service';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit, OnDestroy {
  public content: Entry<any> | undefined;
  public globalContent$: Observable<Entry<any>>;
  public changePasswordForm = new FormGroup({
    newPassword: new FormControl('', [
      Validators.required,
      Validators.minLength(6)
    ]),
    confirmPassword: new FormControl('', [
      Validators.required,
      this.matchValues('newPassword')
    ])
  });
  public waiting: boolean = false;
  public email: string = '';
  public mode: string = '';
  public resetLoaded: boolean = false;
  public canReset: boolean = false;
  public resetCodeError: boolean = false;
  public resetFormError: boolean = false;

  public recoverLoaded: boolean = false;
  public emailRecovered: boolean = false;
  public resetEmailSent: boolean = false;
  public resetEmailError: boolean = false;
  public recoverCodeError: boolean = false;

  public verifyLoaded: boolean = false;
  public verifyConfirm: boolean = false;
  public verifyError: boolean = false;

  private subscriptions: Array<Subscription> = [];
  private actionCode: string = '';

  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private localizationService: LocalizationService,
    private globalContentService: GlobalContentService
  ) {}

  ngOnInit() {
    this.content = this.activatedRoute.snapshot.data.content;
    this.globalContent$ = this.globalContentService.getGlobalContent();
    this.handleUserManagement(this.getParameterByName('mode'));

    this.subscriptions.push(
      this.changePasswordForm.get('newPassword').valueChanges.subscribe(() => {
        this.changePasswordForm.controls.confirmPassword.updateValueAndValidity();
      })
    );
  }

  get confirmPassword() {
    return this.changePasswordForm.get('confirmPassword');
  }

  get newPassword() {
    return this.changePasswordForm.get('newPassword');
  }

  hasError(formControl: AbstractControl, validator: string) {
    return (
      formControl.touched &&
      formControl.dirty &&
      formControl.hasError(validator)
    );
  }

  getParameterByName(param: string): string {
    return this.activatedRoute.snapshot.queryParams[param];
  }

  handleUserManagement(mode: string): void {
    const actionCode = this.getParameterByName('oobCode');
    this.mode = mode;
    switch (mode) {
      case 'resetPassword':
        this.handleResetPassword(actionCode);
        break;
      case 'recoverEmail':
        this.handleRecoverEmail(actionCode);
        break;
      case 'verifyEmail':
        this.handleVerifyEmail(actionCode);
        break;
      default:
        console.error('Error: invalid user management case');
        break;
    }
  }

  handleResetPassword(actionCode: string) {
    if (actionCode) {
      this.authService
        .verifyPasswordResetCode(actionCode)
        .then((email: string) => {
          this.resetLoaded = true;
          this.canReset = true;
          this.actionCode = actionCode;
          this.email = email;
        })
        .catch(error => {
          this.resetLoaded = true;
          this.resetCodeError = true;
          // Invalid or expired action code. Ask user to try to reset the password again.
          console.error(error);
        });
    } else {
      this.resetLoaded = true;
    }
  }

  resetPassword(): void {
    Object.values(this.changePasswordForm.controls).forEach(control => {
      control.markAsDirty();
      control.markAsTouched();
      control.updateValueAndValidity();
    });
    if (this.changePasswordForm.valid && this.email && this.actionCode) {
      this.waiting = true;
      this.authService
        .confirmPasswordReset(this.actionCode, this.newPassword.value)
        .then(response => {
          this.waiting = false;
          // sign-in the user directly
          this.authService
            .signInWithEmailAndPassword(this.email, this.newPassword.value)
            .then(() => {
              this.localizationService.navigate(['profile']);
            });
        })
        .catch(error => {
          // Error occurred during confirmation. The code might have expired or the password is too weak.
          this.resetFormError = true;
          this.waiting = false;
          console.error(error);
        });
    }
  }

  handleRecoverEmail(actionCode: string) {
    let restoredEmail: string | null = null;
    if (actionCode) {
      this.authService
        .checkActionCode(actionCode)
        .then(info => {
          // Get the restored email address
          restoredEmail = info['data']['email'];

          // Revert to the old email
          return this.authService.applyActionCode(actionCode);
        })
        .then(() => {
          this.recoverLoaded = true;
          // Account email reverted to restoredEmail
          this.emailRecovered = true;

          // You might also want to give the user the option to reset their password in case the account was compromised:
          this.authService
            .resetPassword(
              { email: restoredEmail },
              this.localizationService.locale.getValue()
            )
            .then(() => {
              // Password reset confirmation sent. Ask user to check their email.
              this.resetEmailSent = true;
            })
            .catch(error => {
              // Error encountered while sending password reset code.
              this.resetEmailError = true;
              console.error(error);
            });
        })
        .catch(error => {
          this.recoverLoaded = true;
          // Invalid code.
          this.recoverCodeError = true;
          console.error(error);
        });
    } else {
      this.recoverLoaded = true;
    }
  }

  handleVerifyEmail(actionCode: string) {
    if (actionCode) {
      this.authService
        .applyActionCode(actionCode)
        .then(function(resp) {
          this.verifyLoaded = true;
          // Email address has been verified.
          this.verifyConfirm = true;
        })
        .catch(error => {
          this.verifyLoaded = true;
          this.verifyError = true;
          console.error(error);
        });
    } else {
      this.verifyLoaded = true;
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
  }

  private matchValues(
    matchTo: string // name of the control to match to
  ): (AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      return !!control.parent &&
        !!control.parent.value &&
        control.value === control.parent.controls[matchTo].value
        ? null
        : { notSame: true };
    };
  }
}
